import React, { useEffect, useMemo, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG } from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { OneVerifyIntegrationCategory } from "@common-shared/types/integrations.types"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SpinnerElement } from "@front-shared/elements/spinner.element"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { INTEGRATIONS_CONFIG } from "../../config/integrations.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import type { IOneVerifyIntegration } from "../../types/integrations.types"
import type { IEraUploadReviewState } from "../../types/integrations.types"
import { RequestIntegrationPopupComponent } from "../popups/requestIntegrationPopup.component"
import { EraUploadReviewComponent } from "./eraUpload/eraUploadReview.component"
import { IntegrationCategoriesComponent } from "./integrationCategories.component"
import { IntegrationDetailComponent } from "./integrationDetail.component"
import { IntegrationsListComponent } from "./integrationsList.component"
import { UploadEraIntegrationDetailComponent } from "./uploadEraIntegrationDetail.component"

export const IntegrationsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const isMobile = useIsMobile()

    useHasPermissions(OneVerifyPermission.managePracticeIntegrations)

    useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_INTEGRATIONS,
        undefined,
        ROUTES_CONFIG.profileUrl,
    )

    const isCsvUploadEnabled =
        useIsFeatureFlagEnabled(FeatureFlag.ONE_VERIFY_ERA_UPLOAD_CSV) ?? false

    // TODO: Remove this once the early release features are removed
    const earlyReleaseFeaturesEligible =
        useIsFeatureFlagEnabled(
            FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
        ) ?? false

    const runtimeState = useAppSelector(runtimeSelector)

    const [activeCategory, setActiveCategory] =
        useState<OneVerifyIntegrationCategory>(
            OneVerifyIntegrationCategory.eraData,
        )

    const [selectedIntegration, setSelectedIntegration] =
        useState<IOneVerifyIntegration | null>(null)

    const [
        isRequestIntegrationPopupActive,
        setIsRequestIntegrationPopupActive,
    ] = useState<boolean>(false)

    const [eraUploadReviewState, setEraUploadReviewState] =
        useState<IEraUploadReviewState | null>(null)

    const integrationsForCategory = useMemo(() => {
        switch (activeCategory) {
            case OneVerifyIntegrationCategory.eraData:
                return INTEGRATIONS_CONFIG.eraIntegrations.filter(
                    integration =>
                        earlyReleaseFeaturesEligible ||
                        integration.id !==
                            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload
                                .integrationId,
                )
            case OneVerifyIntegrationCategory.ehr:
                return INTEGRATIONS_CONFIG.ehrIntegrations
        }
    }, [activeCategory, earlyReleaseFeaturesEligible])

    const isUploadEraSelected =
        selectedIntegration?.id ===
        COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.integrationId

    const isReviewActive =
        earlyReleaseFeaturesEligible &&
        !!eraUploadReviewState &&
        isUploadEraSelected

    useEffect(() => {
        setSelectedIntegration(integrationsForCategory[0] ?? null)
        setEraUploadReviewState(null)
    }, [integrationsForCategory])

    useEffect(() => {
        if (!isUploadEraSelected) {
            setEraUploadReviewState(null)
        }
    }, [isUploadEraSelected])

    const pageTitle = isReviewActive ? "Review ERA Upload" : "Integrations"

    return runtimeState.enabledFeatureFlags ? (
        <div className="pb-32 relative max-w-full">
            <PageHelmetElement
                title={pageTitle}
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div className="max-w-[960px] w-full overflow-x-hidden">
                <PrimaryText
                    className="sm:mt-[64px]"
                    typography={isMobile ? "h2" : "h1"}
                >
                    Integrations
                </PrimaryText>

                <PrimaryText className="mt-[8px] max-w-[640px]" weight="medium">
                    Integrate your data sources to power ERA ingestion and
                    improve coverage accuracy.
                </PrimaryText>
            </div>

            {isReviewActive ? (
                <EraUploadReviewComponent
                    className="mt-[24px]"
                    state={eraUploadReviewState}
                    onClose={() => {
                        setEraUploadReviewState(null)
                    }}
                    onReuploadWithNewFile={setEraUploadReviewState}
                    onUploadSuccess={() => {
                        setEraUploadReviewState(null)
                    }}
                />
            ) : (
                <div className="max-w-[960px] w-full overflow-x-hidden">
                    <IntegrationCategoriesComponent
                        className="mt-[24px]"
                        activeCategory={activeCategory}
                        onCategoryChange={setActiveCategory}
                    />

                    <IntegrationsListComponent
                        className="mt-[16px]"
                        integrations={integrationsForCategory}
                        selectedIntegration={selectedIntegration}
                        onSelectIntegration={setSelectedIntegration}
                        onRequestIntegration={() => {
                            setIsRequestIntegrationPopupActive(true)
                        }}
                    />

                    <RequestIntegrationPopupComponent
                        isActive={isRequestIntegrationPopupActive}
                        integrationCategory={activeCategory}
                        onDone={() => {
                            setIsRequestIntegrationPopupActive(false)
                        }}
                    />

                    {earlyReleaseFeaturesEligible &&
                    isUploadEraSelected &&
                    selectedIntegration ? (
                        <UploadEraIntegrationDetailComponent
                            key={selectedIntegration.id}
                            className="mt-[24px]"
                            integration={selectedIntegration}
                            isCsvUploadEnabled={isCsvUploadEnabled}
                            onValidationDone={setEraUploadReviewState}
                        />
                    ) : (
                        <IntegrationDetailComponent
                            key={selectedIntegration?.id.toString()}
                            className="mt-[24px]"
                            integration={selectedIntegration}
                        />
                    )}
                </div>
            )}
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
